import React, { useEffect, useState } from 'react';

import { LineInfo } from './LineInfo';
import { Table } from './Table';
import { Button } from './buttons/Button';
import { Close } from '../assets/Close';
import { Pencil } from '../assets/Pencil';
import { useEstimatedDeliveryDate } from '../hooks/useShippingRates';
import { Customer, Order } from '../pages/types';
import { ReplacementOrderHeader } from '../tableHeaders';
import {
  formatCountryCode,
  formatDeliveryDateEstimates,
  formatDeliveryDateEstimatesFromShippingOptions,
  generateFullName,
  SHIPMENT_DISPLAY_VALUE,
} from '../utils';

interface ModificationOrderProps {
  customer: Customer;
  order: Order;
  onConfirm: () => void;
  onCancel: () => void;
}

const formatShippingData = (
  shippingAddress: string | JSX.Element,
  shippingMethod: string,
  estimatedDeliveryDates: string
) => {
  return (
    <div className="flex-col text-start">
      <div className="flex flex-row">
        <strong className="mr-1 min-w-[144px]">Shipping Address:</strong> {shippingAddress}
      </div>
      <div className="flex flex-row">
        <strong className="mr-1">Shipping Method:</strong> {shippingMethod}
      </div>
      <div className="flex flex-row">
        <strong className="mr-1">Estimated Delivery Date:</strong> {estimatedDeliveryDates}
      </div>
    </div>
  );
};

const getOrderProductData = (order: Order) => {
  return order.products.map((product: any, index: number) => {
    return {
      'Pet Name': product.pet_plan?.pet_name,
      Quantity: product.quantity,
      'Product Code': <div className="max-w-[180px]">{product.code}</div>,
      'Product Description': (
        <div className="max-w-[722px]">{product.description_bark ?? '--'}</div>
      ),
      index,
      ...product,
    };
  });
};

export const ModificationOrderModal = ({
  customer,
  order,
  onConfirm,
  onCancel,
}: ModificationOrderProps) => {
  const getOriginalShippingData = (order: Order) => {
    const orderInfo = formatShippingData(
      originalShippingAddress,
      SHIPMENT_DISPLAY_VALUE[order?.shipping_method ?? ''] ?? '--',
      formatDeliveryDateEstimates(order)
    );
    return [{ 'Original Order': orderInfo }];
  };

  const getNewShippingData = (order: Order) => {
    const shippingAddress = newShippingAddress || (
      <strong className="underline cursor-pointer mr-1">Select or add new shipping address</strong>
    );
    const shippingAddressWithPencilJsx = (
      <div className="flex flex-row items-center gap-1">
        {shippingAddress}
        <Pencil className="w-4 h-4 cursor-pointer" />
      </div>
    );
    const estimatedDeliveryDates =
      newShippingAddress && !isLoadingShippingRates
        ? formatDeliveryDateEstimatesFromShippingOptions(
            shippingOptions,
            order?.shipping_method ?? undefined
          )
        : '--';

    const orderInfo = formatShippingData(
      shippingAddressWithPencilJsx,
      SHIPMENT_DISPLAY_VALUE[order?.shipping_method ?? ''] ?? '--',
      estimatedDeliveryDates
    );
    return [{ 'New Order': orderInfo }];
  };

  const [originalShippingAddress, setOriginalShippingAddress] = useState<string>('');
  // TODO(SWE-6123): Populate with result of address selection/addition modal
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [newShippingAddress, setNewShippingAddress] = useState<string | undefined>(undefined);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [newPostalCode, setNewPostalCode] = useState<string | undefined>(undefined);
  const productsHasFrozen = order.products.some((selectedProduct) => selectedProduct.is_frozen);
  const productsHasAmbient = order.products.some((selectedProduct) => !selectedProduct.is_frozen);

  const { shippingOptions, isLoadingShippingRates } = useEstimatedDeliveryDate(
    customer.id,
    productsHasFrozen,
    productsHasAmbient,
    /* zipCodeOverride = */ newPostalCode
  );

  useEffect(() => {
    const primaryShippingAddress = customer.addresses?.find(
      (address) => address.type === 'SHIPPING' && address.is_primary
    );
    if (primaryShippingAddress) {
      const { address1, address2, city, state, zip, country } = primaryShippingAddress;

      const formattedCountry = formatCountryCode(country);
      setOriginalShippingAddress(
        [address1, address2, city, state, zip, formattedCountry].filter(Boolean).join(', ')
      );
    }
  }, [customer.addresses]);

  return (
    <div className="fixed inset-0 flex items-center justify-center m-4 z-50 modal">
      <div className="bg-white rounded-lg shadow-md p-4 min-w-[700px] w-[1100px] relative border overflow-y-auto max-h-[90vh]">
        <Close className="absolute top-1 right-1 w-8 h-8 cursor-pointer" onClick={onCancel} />
        <div className="flex flex-col gap-[24px] w-full">
          <div>
            <h1 className="font-bold text-left">Send Order to a Different Address</h1>
            <LineInfo
              title="Customer"
              value={`${generateFullName(customer.first_name, customer.last_name)} (${
                customer.email
              })`}
            />
          </div>
          <div>
            <h3 className="font-bold text-lg">Shipping</h3>
            <div className="flex flex-row gap-[16px]">
              <div className="flex-1 min-w-0 max-w-[580px]">
                <Table header={['Original Order']} data={getOriginalShippingData(order)} />
              </div>
              <div className="flex-1 min-w-0 max-w-[580px]">
                <Table header={['New Order']} data={getNewShippingData(order)} />
              </div>
            </div>
          </div>
          <div className="w-full">
            <h3 className="font-bold text-lg">Products</h3>
            <div className="w-full">
              <Table header={ReplacementOrderHeader} data={getOrderProductData(order)} />
            </div>
          </div>
          <div className="flex justify-center py-4">
            <Button
              isDisabled={newShippingAddress === undefined}
              label="Confirm"
              variant={newShippingAddress === undefined ? 'secondary' : 'primary'}
              // TODO(SWE-6125): functionality for this button
              onClick={() => console.log('Create modification order')}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
